import { createSlice } from '@reduxjs/toolkit';
import { capitalizeString, sortAsc } from '@strata/helper-functions';
import { FirstFillStatusIcon } from '@strata/react-status-icons';
import { ReferralStatusIndicator } from '@strata/react-status-indicators';
import dayjs from 'dayjs';

import { getReferralEvents, getReferralFilterOptions } from 'api/agoraAPI';
import { getLiveReferralBids } from 'api/auctionAPI';
import { getReferral as getClientReferral, getReferralBids } from 'api/clientAPI';
import { getPerformanceByReferralId } from 'api/performanceAPI';
import { getReferral as getTreatmentReferral } from 'api/treatmentAPI';
import { OnboardingStatusIndicatorMap } from 'services/constants';
import { handleError } from 'services/Error';

export const referralsSlice = createSlice({
    name: 'referrals',
    initialState: {
        referralFilterCategories: undefined,
        referralDetails: {},
        referralEvents: {},
        performanceEntries: {},
        bids: {},
    },
    reducers: {
        setReferralFilterCategories(state, action) {
            return {
                ...state,
                referralFilterCategories: action.payload,
            };
        },
        setReferralEvents(state, action) {
            const { referralId, referralEvents } = action.payload;
            return {
                ...state,
                referralEvents: {
                    ...state.referralEvents,
                    [referralId]: referralEvents,
                },
            };
        },
        setReferralDetails(state, action) {
            const { referralDetails } = action.payload;
            return {
                ...state,
                referralDetails: {
                    ...state.referralDetails,
                    [referralDetails.referralId]: referralDetails,
                },
            };
        },
        setBidsForReferral(state, action) {
            const { referralId, bids } = action.payload;
            return {
                ...state,
                bids: {
                    ...state.bids,
                    [referralId]: bids,
                },
            };
        },
        setPerformanceEntriesForReferral(state, action) {
            const { referralId, performanceEntries } = action.payload;
            return {
                ...state,
                performanceEntries: {
                    ...state.performanceEntries,
                    [referralId]: performanceEntries,
                },
            };
        },
    },
});

export const {
    setReferralFilterCategories,
    setReferralEvents,
    setBidsForReferral,
    setPerformanceEntriesForReferral,
    setDefaultSupplierForReferral,
    setReferralDetails,
} = referralsSlice.actions;

export const fetchReferralEvents = (referralId) => async (dispatch) => {
    try {
        const referralEventsRaw = await getReferralEvents(referralId);

        const referralEvents = {};

        referralEventsRaw.forEach((event) => {
            referralEvents[event.event_type] = {
                referralEventId: event.referral_event_id,
                referralId: event.referralId,
                eventType: event.event_type,
                data: event.data,
            };
        });
        dispatch(setReferralEvents({ referralId, referralEvents }));
    } catch (err) {
        handleError('An error occurred while referral events.', err);
    }
};

export const fetchPerformanceEntries = (referralId) => async (dispatch) => {
    try {
        const performanceEntries = await getPerformanceByReferralId(referralId);
        dispatch(setPerformanceEntriesForReferral({ referralId, performanceEntries }));
    } catch (err) {
        handleError('An error occurred retrieving performance information.', err);
    }
};

export const fetchReferralBids =
    ({ referralId, payerId, auctionStart, auctionEnd }) =>
    async (dispatch) => {
        try {
            let bids = [];
            if (auctionStart && auctionEnd) {
                // Get bids from correct endpoint if referral is Live Referral
                const referralBids = await (dayjs().isBefore(auctionEnd)
                    ? getLiveReferralBids(referralId)
                    : getReferralBids({
                          referralId,
                          payerId,
                          auctionStart,
                          auctionEnd,
                      }));
                bids = referralBids.map((bid) => ({
                    bidAmt: bid.bid_amt != null ? Number(bid.bid_amt) : bid.bid_amt,
                    referentPriceType: bid.referent_price_type,
                    supplierNpi: bid.supplier_npi,
                    bidTimestamp: bid.bid_timestamp,
                }));
            }
            dispatch(setBidsForReferral({ referralId, bids }));
        } catch (err) {
            handleError('An error occurred while fetching Live Referral bids.', err);
        }
    };

export const fetchReferralFilterCategories =
    (timeFrameFilter, payerSegmentMap) => async (dispatch) => {
        try {
            const response = await getReferralFilterOptions({ timeFrameFilter });

            const filterOptions = {
                ...response,
                referral_status: response.referral_status?.map((item) => ({
                    ...item,
                    text:
                        ReferralStatusIndicator.StatusMessages[item.value] ??
                        OnboardingStatusIndicatorMap[item.value]?.message ??
                        item.value,
                })),
                first_fill_status: response.first_fill_status?.map((item) => ({
                    ...item,
                    text: FirstFillStatusIcon.StatusTextMap[item.value]?.() ?? item.value,
                })),
                supplier_npi: response.supplier_npi?.map((item) => ({
                    ...item,
                    text: item.text == null ? `Unknown Supplier (${item.value})` : item.text,
                })),
                lite: response.lite?.map((item) => ({
                    value: item.value === true ? '1' : '0',
                    text: item.value === true ? 'Lite' : 'Contracted',
                })),
                benefit: response.benefit?.map((item) => ({
                    ...item,
                    text: capitalizeString(item.value),
                })),
                is_brand: response.is_brand?.map((item) => ({
                    value: item.value === true ? '1' : '0',
                    text: item.value === true ? 'Brand' : 'Generic',
                })),
                segment_id: response.segment_id?.map((item) => ({
                    ...item,
                    text: payerSegmentMap?.[item.value]?.segment_name,
                })),
            };

            const filterCategories = [
                { key: 'segment_id', title: 'Health Plan' },
                { key: 'referral_status', title: 'Status' },
                { key: 'first_fill_status', title: 'First Fill Status' },
                { key: 'supplier_npi', title: 'Servicing Supplier' },
                { key: 'lite', title: 'Supplier Contract Level' },
                { key: 'claims_status', title: 'Claims Status' },
                { key: 'route_name', title: 'Route Rule' },
                { key: 'benefit', title: 'Benefit' },
                { key: 'is_brand', title: 'Product Type' },
                { key: 'therapeutic_category', title: 'Therapeutic Category' },
            ].map((category) => ({
                ...category,
                customValues: filterOptions[category.key]?.sort((a, b) =>
                    sortAsc(a.text ?? a.value, b.text ?? b.value),
                ),
            }));

            dispatch(setReferralFilterCategories(filterCategories));
        } catch (err) {
            handleError('An error occurred while retrieving referral filter options.', err);
        }
    };

export const addReferralEvent = (referralEvent) => async (dispatch, getState) => {
    try {
        const referralEventsState = getState().referrals.referralEvents;
        let updatedState = null;
        if (referralEventsState[referralEvent.referralId]) {
            updatedState = {
                ...referralEventsState,
                [referralEvent.referralId]: {
                    ...referralEventsState[referralEvent.referralId],
                    [referralEvent.eventType]: referralEvent,
                },
            };
        } else {
            updatedState = {
                ...referralEventsState,
                [referralEvent.referralId]: {
                    [referralEvent.eventType]: referralEvent,
                },
            };
        }
        dispatch(setReferralEvents(updatedState));
    } catch (err) {
        handleError('An error occurred adding a new event to a referral event list.', err);
    }
};

export const replaceReferralEvents = (referralEvents) => async (dispatch) => {
    try {
        if (referralEvents.length !== 0) {
            const referralEventMap = { [referralEvents[0].referral_id]: {} };
            referralEvents.forEach((referralEvent) => {
                referralEventMap[referralEvent.referral_id][referralEvent.event_type] =
                    referralEvent;
            });
            dispatch(setReferralEvents(referralEventMap));
        } else {
            dispatch(setReferralEvents({}));
        }
    } catch (err) {
        handleError('An error occurred when replacing the referral event list.', err);
    }
};

export const fetchReferralDetails =
    ({ payerId, referralId, notFoundCallback }) =>
    async (dispatch) => {
        try {
            const treatmentReferral = await getTreatmentReferral(referralId, payerId);
            if (Object.keys(treatmentReferral).length === 0) {
                notFoundCallback?.();
                return;
            }
            const referralDetails = {
                referralId: treatmentReferral.referral_id,
                treatmentId: treatmentReferral.treatment_id,
                payerId,
                benefit: treatmentReferral.benefit_type,
                friendlyId: treatmentReferral.friendly_id,
                isBrand: treatmentReferral.is_brand,
                memberId: treatmentReferral.member.member_id,
                patientDateOfBirth: treatmentReferral.member.dob,
                patientFirstName: treatmentReferral.member.first_name,
                patientLastName: treatmentReferral.member.last_name,
                placeOfService: treatmentReferral.place_of_service?.trim(),
                prescriberNpi: treatmentReferral.prescription_info?.prescriber_npi,
                productQty: treatmentReferral.prescription_info?.product_qty,
                productServiceId: treatmentReferral.product_service_id,
                productServiceOid: treatmentReferral.product_service_oid,
                receivedTime: treatmentReferral.created_at,
                referentPriceType:
                    treatmentReferral.servicing_info?.servicing_rate?.referent_price_type,
                referralExpiration: treatmentReferral.referral_period?.expiration,
                originalReferralExpiration:
                    treatmentReferral.referral_period?.original_expiration_date,
                referralStart: treatmentReferral.referral_period?.start,
                contractedDate: treatmentReferral.contracted_date,
                outreachDate: treatmentReferral.outreach_date,
                referralStatus: treatmentReferral.referral_status.trim(),
                segmentId: treatmentReferral.segment_id,
                specialInstructions: treatmentReferral.prescription_info?.special_instructions,
                supplierNpi: treatmentReferral.servicing_info?.servicing_parent_pharmacy_npi,
                winningBidRate: treatmentReferral.servicing_info?.servicing_rate?.rate,
                lite: treatmentReferral.lite,
                routeCode: treatmentReferral.route_code,
                routeName: treatmentReferral.route_name,
                copayAssistanceMessage: treatmentReferral.copay_assistance_message
                    ? {
                          title: treatmentReferral.copay_assistance_message.title,
                          shortMessage: treatmentReferral.copay_assistance_message.short_message,
                          longMessage: treatmentReferral.copay_assistance_message.long_message,
                      }
                    : undefined,
                grandfathered: treatmentReferral.grandfathered,
            };

            // Fetch referral details from the client-svc
            const clientReferral = await getClientReferral(referralId, payerId);
            if (Object.keys(clientReferral).length > 0) {
                referralDetails.auctionEnd = clientReferral.auction_end;
                referralDetails.auctionStart = clientReferral.auction_start;
                referralDetails.defaultRate = clientReferral.default_rate;
                referralDetails.diagnosisDisplayName = clientReferral.dx_display_name;
            }

            dispatch(setReferralDetails({ referralDetails }));
        } catch (err) {
            handleError('An error occurred while fetching referral details.', err);
        }
    };
